/* @jsx h */
import { defineComponent, h, ref, computed } from "vue";
import { useQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import { RelativeTimeLabel } from "@lemmonhq/uikit";
import { useTrpc } from "@app/vue/providers/index";
import { AppSubpage, AppSubpageContent } from "@app/vue/components/AppSubpage";
import { AppSubpageHeader } from "@app/vue/components/AppSubpageHeader";
import type { Automation, AutomationTriggerEvent } from "@lemmonhq/types";
import { AutomationEditor } from "./AutomationEditor";

// ─── Config ──────────────────────────────────────────────────────────────────

const TRIGGER_META: Record<AutomationTriggerEvent, { label: string; icon: string; iconBg: string; iconColor: string }> = {
  reservation_completed: { label: "Reservation Completed", icon: "event_available", iconBg: "bg-success/10",  iconColor: "text-success" },
  reservation_created:   { label: "Reservation Booked",    icon: "event",           iconBg: "bg-info/10",     iconColor: "text-info" },
  order_completed:       { label: "Order Completed",        icon: "receipt_long",    iconBg: "bg-primary/10",  iconColor: "text-primary" },
  birthday:              { label: "Birthday",               icon: "cake",            iconBg: "bg-warning/10",  iconColor: "text-warning" },
  member_created:        { label: "Member Added to List",   icon: "group_add",       iconBg: "bg-secondary/10", iconColor: "text-secondary" },
};

function delayLabel(minutes: number): string {
  if (minutes === 0) return "Immediately";
  if (minutes < 60)  return `${minutes}m after`;
  if (minutes < 1440) return `${minutes / 60}h after`;
  return `${minutes / 1440}d after`;
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export type LoyaltyAutomationsPageProps = {
  scope?: "shop" | "group";
  canManage?: boolean;
};

export const LoyaltyAutomationsPage = defineComponent<LoyaltyAutomationsPageProps>({
  name: "LoyaltyAutomationsPage",
  props: ["scope", "canManage"],
  setup(props) {
    const trpc = useTrpc();
    const queryClient = useQueryClient();
    const scope = computed(() => props.scope ?? "shop");
    const canManage = computed(() => props.canManage ?? true);
    const queryKey = computed(() => ["automations", scope.value]);
    const showEditor = ref(false);
    const editingAutomation = ref<Automation | null>(null);

    const automationsQuery = useQuery({
      queryKey,
      queryFn: () => scope.value === "group"
        ? trpc.shopAutomations.groupList.query()
        : trpc.shopAutomations.list.query(),
      staleTime: 30_000,
    });

    const automations = computed<Automation[]>(() => automationsQuery.data?.value ?? []);

    const toggleMutation = useMutation({
      mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
        scope.value === "group"
          ? trpc.shopAutomations.groupToggleEnabled.mutate({ id, enabled })
          : trpc.shopAutomations.toggleEnabled.mutate({ id, enabled }),
      onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKey.value }),
    });

    const togglingId = ref<string | null>(null);

    const handleToggle = (a: Automation) => {
      togglingId.value = a.id;
      toggleMutation.mutate(
        { id: a.id, enabled: !a.enabled },
        { onSettled: () => { togglingId.value = null; } },
      );
    };

    const stats = computed(() => ({
      total:   automations.value.length,
      active:  automations.value.filter((a) => a.enabled).length,
      sent:    automations.value.reduce((s, a) => s + a.total_sent, 0),
    }));

    return () => (
      <div class="flex flex-1 flex-col">
        <AppSubpageHeader icon="bolt" title="Automations" description="Event-driven messages sent automatically when something happens">
          {{
            actions: () => canManage.value ? (
              <button
                type="button"
                class="btn btn-primary btn-sm gap-2 shadow-lg hover:shadow-primary/25"
                onClick={() => { editingAutomation.value = null; showEditor.value = true; }}
              >
                <span class="material-symbols-outlined text-lg">add</span>
                New Automation
              </button>
            ) : null,
          }}
        </AppSubpageHeader>

        <AppSubpage>
          <AppSubpageContent>
            {automationsQuery.isLoading.value ? (
              <div class="flex items-center justify-center h-64">
                <span class="loading loading-spinner loading-lg text-primary opacity-60" />
              </div>
            ) : automationsQuery.isError.value ? (
              <div class="flex flex-col items-center justify-center h-64 gap-3">
                <span class="material-symbols-outlined text-5xl text-error/60">error_outline</span>
                <p class="text-sm text-base-content/60">Failed to load automations</p>
                <button class="btn btn-sm btn-outline btn-error gap-2" onClick={() => automationsQuery.refetch()}>
                  <span class="material-symbols-outlined text-base">refresh</span>Retry
                </button>
              </div>
            ) : (
              <div class="space-y-5">
                {/* Stats */}
                <div class="grid grid-cols-3 gap-2 sm:gap-3">
                  {[
                    { icon: "bolt",          label: "Total",        value: stats.value.total,  bg: "bg-primary/10",  clr: "text-primary" },
                    { icon: "check_circle",  label: "Active",       value: stats.value.active, bg: "bg-success/10",  clr: "text-success" },
                    { icon: "send",          label: "Msgs Sent",    value: stats.value.sent.toLocaleString(), bg: "bg-info/10", clr: "text-info" },
                  ].map((s) => (
                    <div key={s.label} class="flex flex-col gap-2 bg-base-100 rounded-xl border border-base-300/40 px-3 py-3 sm:px-5 sm:py-4 l-box-shadow">
                      <div class={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg ${s.bg} flex items-center justify-center`}>
                        <span class={`material-symbols-outlined text-base sm:text-lg ${s.clr}`}>{s.icon}</span>
                      </div>
                      <div>
                        <p class="text-[10px] sm:text-xs text-base-content/40 uppercase tracking-wider font-medium truncate">{s.label}</p>
                        <p class={`text-lg sm:text-2xl font-bold leading-tight truncate ${s.clr}`}>{s.value}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Automation list */}
                {automations.value.length === 0 ? (
                  <div class="flex flex-col items-center justify-center py-20 gap-4 text-base-content/40">
                    <div class="w-16 h-16 rounded-2xl bg-base-200 flex items-center justify-center">
                      <span class="material-symbols-outlined text-3xl">bolt</span>
                    </div>
                    <div class="text-center">
                      <p class="font-medium text-base-content/60">No automations yet</p>
                      <p class="text-sm mt-1">Create your first to start sending automatic messages</p>
                    </div>
                    {canManage.value && <button
                      type="button"
                      class="btn btn-primary btn-sm gap-2 mt-1"
                      onClick={() => { editingAutomation.value = null; showEditor.value = true; }}
                    >
                      <span class="material-symbols-outlined text-base">add</span>
                      Create Automation
                    </button>}
                  </div>
                ) : (
                  <div class="space-y-3">
                    {automations.value.map((a) => {
                      const meta = TRIGGER_META[a.trigger_event as AutomationTriggerEvent] ?? TRIGGER_META.reservation_completed;
                      return (
                        <div
                          key={a.id}
                          class={`bg-base-100 border rounded-xl p-4 transition-colors ${canManage.value ? "cursor-pointer hover:border-base-content/20" : ""} ${a.enabled ? "border-base-300/50" : "border-base-300/30 opacity-60"}`}
                          onClick={() => {
                            if (!canManage.value) return;
                            editingAutomation.value = a;
                            showEditor.value = true;
                          }}
                        >
                          <div class="flex items-start gap-4">
                            {/* Trigger icon */}
                            <div class={`w-10 h-10 rounded-xl ${meta.iconBg} flex items-center justify-center shrink-0 mt-0.5`}>
                              <span class={`material-symbols-outlined text-lg ${meta.iconColor}`}>{meta.icon}</span>
                            </div>

                            {/* Info */}
                            <div class="flex-1 min-w-0">
                              <div class="flex items-center gap-2 flex-wrap">
                                <span class="font-semibold text-accent">{a.name}</span>
                                <span class={`text-xs px-1.5 py-0.5 rounded font-semibold ${a.enabled ? "bg-success/15 text-success" : "bg-base-content/10 text-base-content/40"}`}>
                                  {a.enabled ? "Active" : "Paused"}
                                </span>
                              </div>
                              <div class="flex items-center gap-3 mt-1 flex-wrap text-xs text-base-content/50">
                                <span class="flex items-center gap-1">
                                  <span class="material-symbols-outlined text-xs">{meta.icon}</span>
                                  {meta.label}
                                </span>
                                <span class="flex items-center gap-1">
                                  <span class="material-symbols-outlined text-xs">schedule</span>
                                  {delayLabel(a.delay_minutes)}
                                </span>
                                <span class="flex items-center gap-1">
                                  <span class="material-symbols-outlined text-xs">send</span>
                                  {a.total_sent} sent
                                </span>
                                {a.channels.map((ch) => (
                                  <span key={ch} class="flex items-center gap-0.5 capitalize">
                                    <span class="material-symbols-outlined text-xs">
                                      {ch === "email" ? "mail" : ch === "whatsapp" ? "chat" : "textsms"}
                                    </span>
                                    {ch}
                                  </span>
                                ))}
                              </div>
                            </div>

                            {/* Toggle */}
                            {canManage.value && <div class="shrink-0 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                              <button
                                type="button"
                                disabled={togglingId.value === a.id}
                                onClick={() => handleToggle(a)}
                                class={`btn btn-xs gap-1 ${a.enabled ? "btn-warning btn-outline" : "btn-success btn-outline"}`}
                              >
                                {togglingId.value === a.id
                                  ? <span class="loading loading-spinner loading-xs" />
                                  : <span class="material-symbols-outlined text-sm">{a.enabled ? "pause" : "play_arrow"}</span>
                                }
                                {a.enabled ? "Pause" : "Resume"}
                              </button>
                            </div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </AppSubpageContent>

          {showEditor.value && (
            <AutomationEditor
              {...{
                automation: editingAutomation.value ?? undefined,
                scope: scope.value,
                onClose: () => { showEditor.value = false; editingAutomation.value = null; },
                onSaved: () => { showEditor.value = false; editingAutomation.value = null; },
              }}
            />
          )}
        </AppSubpage>
      </div>
    );
  },
});
